import React, { type ReactNode } from "react";
import { AspectRatio, type AspectRatioProps, Flex, type FlexProps, type BoxProps } from "@chakra-ui/react";
import { type Theme } from "contexts/color-theme";
import { useHeaderHeight } from "contexts/header-height";
import { type ChipProps } from "../chips";
import { TextTitle, type TextTitleProps } from "../text";

type BaseDiptychProps = {
  /**
   * Optional chip element to display above the title.
   */
  chip?: {
    text: string;
    variant?: ChipProps["variant"];
  };

  /**
   * The title text.
   */
  title: TextTitleProps["title"];

  /**
   * The html element to use for the title.
   *
   * @default "h3"
   */
  titleAs?: TextTitleProps["titleAs"];

  /**
   * Override the title size set by the Diptych itself.
   *
   * Default varies based on Diptych size.
   * `sm` and `md` Diptychs use an identical size title.
   * `lg` Diptychs use an `xs` title.
   */
  titleSize?: TextTitleProps["size"];

  /**
   * The description text.
   */
  description?: TextTitleProps["description"];

  /**
   * The html element to use for the description.
   *
   * Please use a `div` when passing in MDX values or any other content that could
   * result in a [hydration error](https://nextjs.org/docs/messages/react-hydration-error).
   *
   * @default "p"
   */
  descriptionAs?: TextTitleProps["descriptionAs"];

  /**
   * Optional CTA buttons or links.
   */
  actions?: TextTitleProps["actions"];

  /**
   * Media to display beside the text. Should be either an image or lottie animation
   */
  media: ReactNode;

  /**
   * Optional media aspect ratio.
   * Use `none` to disable the aspect ratio container. Typically only needed if the media includes box shadows that extend beyond the aspect ratio bounds.
   */
  mediaRatio?: AspectRatioProps["ratio"] | "none";

  /**
   * Use this to add a border around the media section. Mostly used to support legacy media elements
   * that don't have adequate contrast against the page background.
   */
  hasBorder?: boolean;

  /**
   * The direction of the media
   */
  direction: "left" | "right";

  /**
   * The direction of the column when on mobile
   *
   * @default "column-reverse"
   */
  columnDirection?: "column" | "column-reverse";

  /**
   * The size of the media
   *
   * @default "md"
   */
  size?: "sm" | "md" | "lg";

  /**
   * The color theme of the diptych.
   *
   * @default "light"
   */
  theme?: Theme;
};

export type DiptychProps = BaseDiptychProps & Omit<FlexProps, "direction" | "title">;

export function Diptych({
  theme = "light",
  size = "md",
  titleAs = "h3",
  descriptionAs = "p",
  columnDirection = "column-reverse",
  chip,
  title,
  titleSize,
  description,
  actions,
  hasBorder,
  media,
  mediaRatio,
  direction,
  gap,
  maxW,
  ...props
}: DiptychProps) {
  const defaultTitleSize = size === "lg" ? "xs" : size;
  const { headerHeight } = useHeaderHeight();
  const mediaProps: BoxProps = {
    borderRadius: { base: "small", md: "bigger" },
    border: hasBorder ? "1px" : "none",
    borderColor: `brand.border.${theme}`,
    flex: size === "lg" ? undefined : 1,
    w: { base: "100%", md: size === "lg" ? "66%" : "50%" },
    maxW: { base: "mediaMaxW.mobile", md: "100%", lg: size === "lg" ? "872px" : "648px" },
    pos: "relative",
  };

  return (
    <Flex
      align="center"
      flexDir={{ base: columnDirection, md: direction === "right" ? "row" : "row-reverse" }}
      gap={
        gap ?? {
          base: "bigger",
          lg: size === "lg" ? "72px" : "huge",
          xl: size === "lg" ? "88px" : "136px",
        }
      }
      justifyContent={{ base: "center", md: "space-between" }}
      maxW={maxW ?? { base: "100%", lg: size === "sm" ? "88%" : "100%" }}
      mx="auto"
      w="100%"
      scrollMarginTop={`${headerHeight + 32}px`}
      {...props}
    >
      <Flex direction="column" mx="auto" flex={size === "lg" ? undefined : 1}>
        <TextTitle
          theme={theme}
          chip={chip}
          title={title}
          titleAs={titleAs}
          description={description}
          descriptionAs={descriptionAs}
          actions={actions}
          size={titleSize ?? defaultTitleSize}
        />
      </Flex>

      {mediaRatio === "none" ? (
        <Flex {...mediaProps}>{media}</Flex>
      ) : (
        <AspectRatio ratio={mediaRatio ?? (size === "lg" ? 872 / 580 : 1 / 1)} overflow="hidden" {...mediaProps}>
          {media}
        </AspectRatio>
      )}
    </Flex>
  );
}
